import React, { useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from "reactstrap";
import { useForm, FieldErrors } from "react-hook-form";
import CommonForm from "Components/Common/CommonForm";
import TruncatedText from "Components/Common/TruncatedText";
import { SponsorFormValues, SponsorRecord } from "../helper/sponsor.types";
import { sponsorFields } from "../helper/sponsorconstant";

/* -----------------------------
 Types
----------------------------- */

interface AddEditSponsorProps {
  isOpen: boolean;
  toggle: () => void;
  existingData: SponsorRecord | null;
  onSave: (record: SponsorFormValues) => void;
  isLoading: boolean;
}

/* -----------------------------
 Component
----------------------------- */

const AddEditSponsor: React.FC<AddEditSponsorProps> = ({
  isOpen,
  toggle,
  existingData,
  onSave,
  isLoading,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<SponsorFormValues>({
    defaultValues: {
      sponsorname: "",
      sponsoraddress: "",
      sponsorcontact: "",
      sponsoremail: "",
      croname: "",
      crocontact: "",
      croemail: "",
    },
    mode: "onChange",
  });

  const watchedSponsorName = watch("sponsorname");
  const watchedCroName = watch("croname");

  const validateSponsorAndCroName = (
    fieldName: "sponsorname" | "croname",
    value: string | number,
  ) => {
    const safeValue = String(value ?? "").trim();
    const otherValue = String(
      fieldName === "croname"
        ? getValues("sponsorname")
        : getValues("croname"),
    ).trim();

    if (!safeValue || !otherValue) return true;

    if (safeValue.toLowerCase() === otherValue.toLowerCase()) {
      return fieldName === "croname"
        ? "CRO Name must not be the same as Sponsor Name"
        : "Sponsor Name must not be the same as CRO Name";
    }

    return true;
  };

  const formFields = sponsorFields.map((field) => {
    if (field.name !== "croname" && field.name !== "sponsorname") {
      return field;
    }

    const originalRules = field.rules || {};
    const originalValidate =
      originalRules.validate as ((value: string) => true | string) | undefined;

    return {
      ...field,
      rules: {
        ...originalRules,
        validate: (value: string) => {
          const crossFieldValidation = validateSponsorAndCroName(
            field.name as "sponsorname" | "croname",
            value,
          );

          if (crossFieldValidation !== true) return crossFieldValidation;

          return originalValidate ? originalValidate(value) : true;
        },
      },
    };
  });

  useEffect(() => {
    if (watchedSponsorName && watchedCroName) {
      trigger(["sponsorname", "croname"]);
    }
  }, [watchedSponsorName, watchedCroName, trigger]);

  /* -----------------------------
   Populate data in edit mode
  ----------------------------- */
  useEffect(() => {
    if (existingData) {
      reset(existingData);
    } else {
      reset({
        sponsorname: "",
        sponsoraddress: "",
        sponsorcontact: "",
        sponsoremail: "",
        croname: "",
        crocontact: "",
        croemail: "",
      });
    }
  }, [existingData, reset, isOpen]);

  /* -----------------------------
   Submit
  ----------------------------- */
  const onSubmit = (values: SponsorFormValues): void => {
    const record: SponsorRecord = existingData
      ? { ...existingData, ...values }
      : values;

    onSave(record);
  };


  /* -----------------------------
   Render
  ----------------------------- */
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      backdrop="static"
      className="common_modal"
      centered
    >
<ModalHeader toggle={toggle} className="fw-bold border-bottom p-3">
  {existingData ? (
    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      Edit Sponsor
      <TruncatedText text={`(${existingData.sponsorname})`} maxWidth="400px" />
    </span>
  ) : (
    "Add Sponsor"
  )}
</ModalHeader>
      {/* Body */}
      <ModalBody>
        <form id="sponsorForm" onSubmit={handleSubmit(onSubmit)}>
          <CommonForm
            fields={formFields}
            control={control}
            errors={errors as FieldErrors}
            columns={2}
            setValue={setValue}
            getValues={getValues}
          />
        </form>
      </ModalBody>

      {/* Footer */}
      <ModalFooter className="bg-white border-top">
        <Button outline onClick={toggle}>
          Cancel
        </Button>

        <Button
          color="primary"
          outline
          type="submit"
          form="sponsorForm"
          disabled={isLoading}
        >
          {isLoading && <Spinner size="sm" className="me-2" />}
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddEditSponsor;
